import { WorkCalendar } from './calendar.js';
import { formatBR } from './dates.js';
/**
 * Método do caminho crítico com calendário de dias úteis.
 * Convenção: ES = início (início do dia), EF = término exclusivo (EF = ES + duração); marco: EF = ES.
 * Trabalho não iniciado nunca é programado antes da data de status; em andamento termina em status + restante.
 */
export function runCpm(input) {
    const cal = new WorkCalendar(input.calendar);
    const acts = input.activities;
    cal.ensure(input.projectStart, input.statusDate);
    const S0 = cal.idx(input.projectStart);
    const statusIdx = cal.idx(input.statusDate);
    const eod = (d) => cal.idxPrev(d) + 1;
    const byId = new Map(acts.map(a => [a.id, a]));
    const succ = new Map(acts.map(a => [a.id, []]));
    const indeg = new Map(acts.map(a => [a.id, 0]));
    const R = new Map();
    const warnings = [];
    for (const a of acts) {
        const dur = a.milestone ? 0 : Math.max(0, Math.round(a.duration || 0));
        R.set(a.id, { id: a.id, duration: dur, es: S0, ef: S0, ls: 0, lf: 0, totalFloat: 0, freeFloat: 0, start: '', finish: '', lateStart: '', lateFinish: '', critical: false, done: false, inCycle: false, status: 'Não iniciada', warnings: [], driven: false, pushed: false });
        for (const l of a.dependencies ?? []) {
            if (!byId.has(l.predecessorId)) {
                R.get(a.id).warnings.push('Predecessora inexistente');
                continue;
            }
            if (l.predecessorId === a.id) {
                R.get(a.id).warnings.push('Atividade vinculada a ela mesma');
                continue;
            }
            succ.get(l.predecessorId).push({ to: a.id, type: l.type, lag: Math.round(l.lag || 0) });
            indeg.set(a.id, indeg.get(a.id) + 1);
        }
    }
    // ordenação topológica (Kahn); o que sobra está em ciclo
    const order = [];
    const deg = new Map(indeg);
    const queue = acts.filter(a => deg.get(a.id) === 0).map(a => a.id);
    while (queue.length) {
        const id = queue.shift();
        order.push(id);
        for (const s of succ.get(id)) {
            deg.set(s.to, deg.get(s.to) - 1);
            if (deg.get(s.to) === 0)
                queue.push(s.to);
        }
    }
    const inOrder = new Set(order);
    const cyc = acts.filter(a => !inOrder.has(a.id));
    if (cyc.length) {
        warnings.push({ type: 'ciclo', severity: 'crit', message: `Ciclo de dependências envolvendo ${cyc.map(a => a.code).join(', ')}.`, ids: cyc.map(a => a.id) });
        for (const a of cyc) {
            const r = R.get(a.id);
            r.inCycle = true;
            r.warnings.push('Participa de um ciclo de dependências');
        }
    }
    // passagem direta
    for (const id of order) {
        const a = byId.get(id), r = R.get(id), d = r.duration;
        let es = S0;
        for (const l of a.dependencies ?? []) {
            const p = R.get(l.predecessorId);
            if (!p || p.inCycle)
                continue;
            const lag = Math.round(l.lag || 0);
            const c = l.type === 'SS' ? p.es + lag : l.type === 'FF' ? p.ef + lag - d : l.type === 'SF' ? p.es + lag - d : p.ef + lag;
            if (c >= es) {
                es = c;
                r.driven = true;
            }
        }
        const cd = a.constraintDate ?? null;
        if (cd && a.constraint === 'SNET')
            es = Math.max(es, cal.idx(cd));
        if (cd && a.constraint === 'FNET')
            es = Math.max(es, eod(cd) - d);
        if (cd && a.constraint === 'MSO') {
            const m = cal.idx(cd);
            if (es > m)
                r.warnings.push(`Restrição "deve iniciar em" ${formatBR(cd)} conflita com as predecessoras`);
            es = m;
        }
        let ef;
        if (a.actualStart) {
            es = cal.idx(a.actualStart);
            if (a.actualFinish)
                ef = Math.max(es + (d ? 1 : 0), eod(a.actualFinish));
            else {
                const pct = Math.min(100, Math.max(0, a.percent ?? 0));
                ef = pct >= 100 ? Math.max(es, statusIdx) : Math.max(statusIdx, es) + Math.ceil(d * (1 - pct / 100));
            }
        }
        else {
            if (statusIdx > S0 && es < statusIdx && !a.actualFinish) {
                es = statusIdx;
                r.pushed = true;
            }
            ef = es + d;
        }
        if (a.actualFinish && !a.actualStart) {
            ef = eod(a.actualFinish);
            es = ef - d;
            r.warnings.push('Término real sem início real');
        }
        r.es = es;
        r.ef = ef;
        if (cd && a.constraint === 'SNLT' && es > cal.idx(cd))
            r.warnings.push(`Início previsto após ${formatBR(cd)} (restrição "iniciar não depois de")`);
        if (cd && a.constraint === 'FNLT' && ef > eod(cd))
            r.warnings.push(`Término previsto após ${formatBR(cd)} (restrição "terminar não depois de")`);
    }
    let projEF = S0;
    for (const id of order)
        projEF = Math.max(projEF, R.get(id).ef);
    // passagem reversa
    for (let i = order.length - 1; i >= 0; i--) {
        const id = order[i], a = byId.get(id), r = R.get(id), d = r.duration;
        let lf = projEF;
        for (const s of succ.get(id)) {
            const n = R.get(s.to);
            if (n.inCycle)
                continue;
            const c = s.type === 'SS' ? n.ls - s.lag + d : s.type === 'FF' ? n.lf - s.lag : s.type === 'SF' ? n.lf - s.lag + d : n.ls - s.lag;
            lf = Math.min(lf, c);
        }
        const cd = a.constraintDate ?? null;
        if (cd && a.constraint === 'FNLT')
            lf = Math.min(lf, eod(cd));
        if (cd && a.constraint === 'SNLT')
            lf = Math.min(lf, cal.idx(cd) + d);
        if (cd && a.constraint === 'MSO')
            lf = Math.min(lf, cal.idx(cd) + d);
        if (a.deadline)
            lf = Math.min(lf, eod(a.deadline));
        r.lf = lf;
        r.ls = lf - d;
        r.totalFloat = r.lf - r.ef;
    }
    // folga livre
    for (const id of order) {
        const r = R.get(id);
        let ff = projEF - r.ef;
        for (const s of succ.get(id)) {
            const n = R.get(s.to);
            if (n.inCycle)
                continue;
            const slack = s.type === 'SS' ? n.es - s.lag - r.es : s.type === 'FF' ? n.ef - s.lag - r.ef : s.type === 'SF' ? n.ef - s.lag - r.es : n.es - s.lag - r.ef;
            ff = Math.min(ff, slack);
        }
        r.freeFloat = Math.max(Math.min(ff, r.totalFloat), r.totalFloat < 0 ? r.totalFloat : 0);
    }
    // datas, criticidade e verificações
    const finishOf = (r) => r.duration > 0 ? cal.date(r.ef - 1) : (r.driven && r.ef > S0 ? cal.date(r.ef - 1) : cal.date(r.es));
    const lateFinishOf = (r) => r.duration > 0 ? cal.date(r.lf - 1) : (r.driven && r.lf > S0 ? cal.date(r.lf - 1) : cal.date(r.ls));
    for (const a of acts) {
        const r = R.get(a.id);
        if (r.inCycle) {
            r.start = a.actualStart ?? input.projectStart;
            r.finish = a.actualFinish ?? input.projectStart;
            r.lateStart = r.start;
            r.lateFinish = r.finish;
            continue;
        }
        r.start = a.actualStart ?? cal.date(r.es);
        r.finish = a.actualFinish ?? finishOf(r);
        r.lateStart = cal.date(r.ls);
        r.lateFinish = lateFinishOf(r);
        if (r.duration === 0) {
            if (!a.actualStart)
                r.start = r.finish;
            r.lateStart = r.lateFinish;
        }
        r.done = !!a.actualFinish || ((a.percent ?? 0) >= 100 && !!a.actualStart);
        r.critical = !r.done && r.totalFloat <= 0;
        r.status = r.done ? 'Concluída' : a.actualStart ? 'Em andamento' : r.pushed ? 'Atrasada no início' : 'Não iniciada';
        if (a.actualStart && a.actualFinish && a.actualFinish < a.actualStart)
            r.warnings.push('Término real anterior ao início real');
        if ((a.percent ?? 0) >= 100 && !a.actualFinish)
            r.warnings.push('Avanço 100% sem término real');
        if (a.actualStart && a.actualStart > input.statusDate)
            r.warnings.push('Início real no futuro');
        if (r.totalFloat < 0 && !r.done)
            r.warnings.push(`Folga negativa de ${-r.totalFloat} dia(s) útil(eis)`);
        if (a.deadline && !r.done && r.finish > a.deadline)
            r.warnings.push(`Previsão ${formatBR(r.finish)} após a data contratual ${formatBR(a.deadline)}`);
        r.warnings = [...new Set(r.warnings)];
    }
    const noPred = acts.filter(a => !(a.dependencies ?? []).length && !a.actualStart && (!a.constraint || a.constraint === 'ASAP'));
    const noSucc = acts.filter(a => !succ.get(a.id).length && !(a.milestone && a.contractual));
    if (noPred.length > 1)
        warnings.push({ type: 'pontas', severity: 'warn', message: `${noPred.length} atividades sem predecessora nem restrição: ${noPred.slice(0, 8).map(a => a.code).join(', ')}`, ids: noPred.map(a => a.id) });
    if (noSucc.length > 1)
        warnings.push({ type: 'pontas', severity: 'warn', message: `${noSucc.length} atividades sem sucessora: ${noSucc.slice(0, 8).map(a => a.code).join(', ')}`, ids: noSucc.map(a => a.id) });
    const threatened = acts.filter(a => { const r = R.get(a.id); return (a.contractual || a.deadline) && !r.done && !r.inCycle && (r.totalFloat < 0 || (!!a.deadline && r.finish > a.deadline)); });
    for (const a of threatened)
        warnings.push({ type: 'marco', severity: 'crit', message: `Marco ${a.code} ameaçado: previsão ${formatBR(R.get(a.id).finish)}${a.deadline ? `, limite ${formatBR(a.deadline)}` : ''}`, ids: [a.id] });
    const items = new Map();
    for (const [k, v] of R) {
        const { driven: _d, pushed: _p, ...rest } = v;
        items.set(k, rest);
    }
    return {
        items, order, warnings, successors: succ,
        finish: acts.length ? cal.date(Math.max(S0, projEF - 1)) : input.projectStart,
        critical: acts.filter(a => items.get(a.id).critical).map(a => a.id),
        threatened: threatened.map(a => a.id)
    };
}
/** Detecta se adicionar/alterar dependências gera ciclo (usado na validação antes de gravar). */
export function findCycle(activities) {
    const succ = new Map(activities.map(a => [a.id, []]));
    const deg = new Map(activities.map(a => [a.id, 0]));
    for (const a of activities)
        for (const l of a.dependencies ?? [])
            if (succ.has(l.predecessorId)) {
                succ.get(l.predecessorId).push(a.id);
                deg.set(a.id, deg.get(a.id) + 1);
            }
    const q = activities.filter(a => deg.get(a.id) === 0).map(a => a.id);
    let seen = 0;
    while (q.length) {
        const id = q.shift();
        seen++;
        for (const t of succ.get(id)) {
            deg.set(t, deg.get(t) - 1);
            if (deg.get(t) === 0)
                q.push(t);
        }
    }
    if (seen === activities.length)
        return null;
    return activities.filter(a => (deg.get(a.id) ?? 0) > 0).map(a => a.code);
}
//# sourceMappingURL=cpm.js.map